export const en = {
    EditSegmentPage: {
        liveRegion: { loading: "Loading segment" }
    },
    SegmentForm: {
        createTitle: "New segment",
        editTitle: "Edit segment",
        name: {
            label: "Segment name",
            placeholder: "Name this segment",
            required: "Enter a name for this segment.",
            tooLong: "Keep the name under {{count}} characters."
        },
        save: "Save",
        discard: "Discard",
        delete: "Delete",
        leaveConfirm: {
            title: "Discard unsaved changes?",
            description: "You have unsaved changes on this page. Leaving now will discard them.",
            leave: "Discard changes",
            stay: "Stay on page"
        },
        deleteConfirm: {
            title: "Delete “{{name}}”?",
            description: "This removes the segment for everyone it's shared with. The people in it and their data are not affected. This can't be undone.",
            cancel: "Cancel"
        },
        modeConfirm: {
            title: "Switch to rules?",
            description: "A segment is either rule-based or hand-picked. Switching to rules discards the people you added.",
            confirm: "Switch and discard",
            cancel: "Keep the list"
        },
        createdToast: "Segment created",
        savedToast: "Changes saved",
        deletedToast: "Segment deleted",
        saveErrorToast: "Couldn't save this segment. Try again.",
        deleteErrorToast: "Couldn't delete this segment. Try again.",
        rules: {
            loading: "Loading employee fields",
            error: "Couldn't load your employee fields.",
            retry: "Try again"
        }
    },
    segmentRules: {
        heading: "Rules",
        matchLabel: "Match",
        matchSuffix: {
            all: "of the following",
            any: "of the following"
        },
        match: {
            all: "all",
            any: "any"
        },
        columns: {
            property: "Property",
            condition: "Condition",
            value: "Value"
        },
        addRule: "Add rule",
        removeRule: "Remove rule",
        property: {
            placeholder: "Select a property",
            unavailable: "This field is no longer available"
        },
        condition: {
            placeholder: "Select a condition",
            unsupported: "This rule uses a condition that can't be edited here. Pick a new one or remove the rule."
        },
        value: {
            placeholder: "Select a value",
            personPlaceholder: "Search for people",
            personNoResults: "No people found",
            personDeactivatedLabel: "{{name}} (Deactivated)",
            personResolving: "Loading…",
            optionNoResults: "No options found",
            selectedPeople: "Selected people",
            selectedOptions: "Selected values",
            rangeStart: "Start",
            rangeEnd: "End",
            periodCount: "Number",
            periodType: "Unit",
            periodTypePlaceholder: "Select a unit",
            periodTypes: {
                Years: "Years",
                Months: "Months",
                Days: "Days"
            }
        },
        conditions: {
            is: "is",
            isExactly: "is exactly",
            isNot: "is not",
            isAnyOf: "is any of",
            isNoneOf: "is none of",
            isBefore: "is before",
            isAfter: "is after",
            isGreaterThan: "is greater than",
            isLessThan: "is less than",
            isBetween: "is between",
            isOlderThan: "occurred more than",
            isWithinTheLast: "occurred less than",
            occurredBetween: "occurred between",
            occursInMoreThan: "occurs in more than",
            occursInLessThan: "occurs in less than",
            occursInBetween: "occurs in between",
            directlyTo: "directly to",
            upThrough: "up through"
        },
        rangeConnector: "and",
        loadingRules: "Loading filter details",
        ruleUnavailable: "{{field}}: filter unavailable",
        unlistedRules: "Some of this segment's rules can't be shown here. Saving will remove them.",
        incomplete: "Finish this rule before saving.",
        missingRangeEnd: "Enter both a start and an end.",
        requiredRule: {
            ownLabel: "Your reporting line",
            ownTitle: "Everyone on your team",
            ownDescription: "Always applies. Keeps this segment within the people you manage.",
            ownHint: "Add a rule to narrow within your team.",
            otherLabel: "Owner's reporting line",
            otherTitle: "Everyone on {{name}}'s team",
            otherTitleFallback: "Everyone on the owner's team",
            otherDescription: "Always applies. Keeps this segment within the people they manage.",
            unlisted: "This segment is narrowed further by a rule that can't be shown here."
        },
        valuesError: "Couldn't load the options for this field.",
        peopleError: "Couldn't load people.",
        retry: "Try again",
        noUsableFields: "None of your employee fields can be used in a rule yet. Add a dropdown, date, number, or person field in Employee fields."
    },
    SegmentMemberPreview: {
        matchSuffix_one: "person matches right now",
        matchSuffix_other: "people match right now",
        empty: "No one matches these rules yet.",
        loading: "Checking who matches...",
        error: "Couldn't check who matches.",
        retry: "Try again",
        staleError: "Couldn't refresh — showing the last results.",
        liveRegion: {
            loadedMore_one: "Loaded {{count}} more person",
            loadedMore_other: "Loaded {{count}} more people",
            refreshed_one: "{{count}} person matches right now",
            refreshed_other: "{{count}} people match right now"
        }
    },
    SegmentModeChooser: {
        heading: "How people are added",
        selected: "Selected",
        ruleBased: {
            title: "Rule-based",
            description: "Auto-updates as people match your rules."
        },
        handPicked: {
            title: "Specific people",
            description: "Hand-pick who's in. The list stays fixed."
        }
    },
    segmentTags: {
        heading: "Tags",
        placeholder: "Add tags",
        suggestedLabel: "Suggested:",
        suggestions: {
            analytics: "Analytics",
            survey: "Survey",
            reviewCycle: "Review cycle",
            onboarding: "Onboarding",
            compensation: "Compensation"
        },
        remove: "Remove tag {{tag}}",
        duplicate: "That tag is already added.",
        suggestionsUpdated_one: "{{count}} suggested tag",
        suggestionsUpdated_other: "{{count}} suggested tags"
    },
    segmentMembers: {
        heading: "People",
        count_one: "{{count}} person",
        count_other: "{{count}} people",
        remove: "Remove {{name}}",
        empty: "No people added yet.",
        required: "Add at least one person to this segment.",
        search: {
            label: "Search people to add",
            placeholder: "Search people to add...",
            loading: "Searching...",
            loadingMore: "Loading more...",
            noResults: "No one matches '{{term}}'.",
            error: "Couldn't search for people. Try again.",
            liveRegion: {
                loadedMore_one: "Loaded {{count}} more person",
                loadedMore_other: "Loaded {{count}} more people"
            }
        }
    },
    SegmentNotFound: {
        heading: "This segment doesn't exist, or it isn't shared with you.",
        backToSegments: "Back to Segments"
    }
};
